import { Grid, Typography, useTheme, useMediaQuery, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import IFund from '../../interfaces/fund/IFund';
import useInvestFundOperations from '../../hooks/fund/useInvestFundOperations';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import IFundOperationsCapitalCall from '../../interfaces/fund/operations/IFundOperationsCapitalCall';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import IFundOperationsDistribution from '../../interfaces/fund/operations/IFundOperationsDistribution';
import investmentFundOperationsLang from '../../langs/investmentFundOperationsLang';
import IFundOperationsShareValue from '../../interfaces/fund/operations/IFundOperationsShareValue';
import { GridColDef, GridRenderCellParams, GridRowModel } from '@mui/x-data-grid';
import styleVariables from '../../../../app/styles/styleVariables';
import { BoxFundItem } from '../expertise/VInvestExpertisePortfolio';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import investmentFundLang from '../../langs/investmentFundLang';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import CFundCashFlowGraph from '../../features/fund/components/CFundCashFlowGraph';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import investmentTypeHistoryLang from '../../features/fund-type/langs/investmentTypeHistoryLang';
import useAppFormatNumber from '../../../../app/features/format/hooks/useAppFormatNumber';
import { TInvestExpertiseTag } from '../../types/expertise/TInvestExpertiseTag';

type VInvestFundOperationsProps = {
	fund: IFund;
	filters: IFundFilters;
};

const h = 280;

const VInvestFundOperations = (props: VInvestFundOperationsProps) => {
	const { fund, filters } = props;

	const { cashFlows, distributions, capitalCalls, shareValues } = useInvestFundOperations(
		fund ? fund.id : null,
		filters,
	);
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [investExpertiseTag, setInvestExpertiseTag] = useState<TInvestExpertiseTag | null>(null);
	useEffect(() => {
		if (fund && fund.typeTag) {
			setInvestExpertiseTag(fund.typeTag);
		}
	}, [fund]);

	return (
		<>
			<CAppSubTitle
				title={t(investmentFundLang.title_operations.tag)}
				icon={<AppCustomIcon tag="fund_operations" className="icon" />}
			/>
			<Grid container spacing={3} sx={{ marginBottom: '30px' }}>
				<Grid item sm={12} xs={12}>
					<Box>
						<Typography variant="h3" component="h3">
							{t(investmentFundOperationsLang.title_cash_flows.tag)}

							<CAppInfoTooltip
								title={t(investmentTypeHistoryLang.title_jcurve_visualization_tooltip.tag)}
							/>
						</Typography>
						<CFundCashFlowGraph fund={fund} cashFlows={cashFlows ?? []} />
					</Box>
				</Grid>
				<Grid item sm={3} xs={12}>
					<Box>
						<Typography variant="h3" component="h3">
							{t(investmentFundOperationsLang.title_distributions.tag)}
						</Typography>
						<CAppPaper variant="outlined" color="primary" sx={{ padding: '0px 10px' }}>
							<DistributionTableDesktop distributions={distributions ?? []} />
						</CAppPaper>
					</Box>
				</Grid>
				<Grid item sm={3} xs={12}>
					<Box>
						<Typography variant="h3" component="h3">
							{t(investmentFundOperationsLang.title_capital_calls.tag)}
						</Typography>

						<CAppPaper variant="outlined" color="primary" sx={{ padding: '0px 10px' }}>
							<CapitalCallTableDesktop capitalCalls={capitalCalls ?? []} />
						</CAppPaper>
					</Box>
				</Grid>
				<Grid item sm={6} xs={12}>
					<Box>
						<Typography variant="h3" component="h3">
							{t(investmentFundOperationsLang.title_share_values.tag)}
						</Typography>

						{isMobile ? (
							<ShareValuesTableMobile
								shareValues={shareValues ?? []}
								investExpertiseTag={investExpertiseTag as TInvestExpertiseTag}
							/>
						) : (
							<CAppPaper
								variant="outlined"
								color=""
								sx={{ padding: '0px 10px', height: h + 'px', overflow: 'hidden' }}
							>
								<ShareValuesTableDesktop
									shareValues={shareValues ?? []}
									investExpertiseTag={investExpertiseTag as TInvestExpertiseTag}
								/>
							</CAppPaper>
						)}
					</Box>
				</Grid>
			</Grid>
		</>
	);
};

type DistributionsTableProps = {
	distributions: IFundOperationsDistribution[];
};
const DistributionTableDesktop = (props: DistributionsTableProps) => {
	const { distributions } = props;

	const { t } = useTranslation();
	const theme = useTheme();

	const [rows, setRows] = useState<GridRowModel[] | null>(null);
	useEffect(() => {
		if (distributions) {
			setRows(distributions.filter((x) => x.yourDistribution !== 0).map((x, index) => ({ ...x, id: index })));
		} else {
			setRows(null);
		}
	}, [distributions]);

	const columns: GridColDef[] = [
		{
			field: 'date',
			headerName: t(investmentFundOperationsLang.column_date.tag),
			minWidth: 100,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'yourDistribution',
			headerName: t(investmentFundOperationsLang.column_amount.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => (
				<AppFormatAmount value={params.value} addSpace currency={params.row.currency} />
			),
		},
	];

	return (
		<Box>
			{rows ? (
				<CAppDataGrid
					className="fund-distributions-table-desktop"
					sx={{
						height: h + 1 + 'px',
						'*::-webkit-scrollbar': {
							width: '5px',
						},
						'*::-webkit-scrollbar-track': {
							backgroundColor: theme.palette.primary.main,
						},

						'*::-webkit-scrollbar-thumb': {
							backgroundColor: theme.palette.secondary.main,
						},
					}}
					color="primary"
					rows={rows}
					columns={columns}
					hideFooter
					disableSelectionOnClick
				/>
			) : (
				''
			)}
		</Box>
	);
};

type CapitalCallTableProps = {
	capitalCalls: IFundOperationsCapitalCall[];
};
const CapitalCallTableDesktop = (props: CapitalCallTableProps) => {
	const { capitalCalls } = props;

	const { t } = useTranslation();
	const theme = useTheme();

	const [rows, setRows] = useState<GridRowModel[] | null>(null);
	useEffect(() => {
		if (capitalCalls) {
			setRows(
				capitalCalls
					.filter((x) => x.amount !== 0)
					.map((x, index) => ({ date: x.date.toString(), amount: x.amount, id: index })),
			);
		} else {
			setRows(null);
		}
	}, [capitalCalls]);

	const columns: GridColDef[] = [
		{
			field: 'date',
			headerName: t(investmentFundOperationsLang.column_date.tag),
			minWidth: 100,
			width: 100,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'amount',
			headerName: t(investmentFundOperationsLang.column_amount.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => (
				<AppFormatAmount value={params.value} addSpace currency={params.row.currency} />
			),
		},
	];

	return (
		<Box>
			{rows ? (
				<CAppDataGrid
					className="fund-capital-calls-table-desktop"
					sx={{
						height: h + 1 + 'px',
						'*::-webkit-scrollbar': {
							width: '5px',
						},
						'*::-webkit-scrollbar-track': {
							backgroundColor: theme.palette.primary.main,
						},

						'*::-webkit-scrollbar-thumb': {
							backgroundColor: theme.palette.secondary.main,
						},
					}}
					color="primary"
					rows={rows}
					columns={columns}
					hideFooter
					disableSelectionOnClick
				/>
			) : (
				''
			)}
		</Box>
	);
};

type ShareValuesTableProps = {
	shareValues: IFundOperationsShareValue[];
	investExpertiseTag: TInvestExpertiseTag;
};
const ShareValuesTableDesktop = (props: ShareValuesTableProps) => {
	const { shareValues, investExpertiseTag } = props;

	const { t } = useTranslation();
	const { formatAmount } = useAppFormatNumber();

	const [rows, setRows] = useState<GridRowModel[] | null>(null);
	useEffect(() => {
		if (shareValues) {
			setRows(shareValues.map((x, index) => ({ ...x, id: index, date: x.reportingPeriod.name })));
		} else {
			setRows(null);
		}
	}, [shareValues]);

	const columns: GridColDef[] = [
		{
			field: 'date',
			headerName: t(investmentFundOperationsLang.column_date.tag),
			minWidth: 100,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'nominalPrice',
			headerName: t(investmentFundOperationsLang.column_nominal_price.tag),
			width: 130,
			align: 'right',
			headerAlign: 'right',
			hide: ['venture_capital', 'private_equity'].includes(investExpertiseTag),
			renderCell: (params: GridRenderCellParams) => formatAmount(params.value, 2, true),
		},
		{
			field: 'navPerShare',
			headerName: t(investmentFundOperationsLang.column_nav_per_share.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => formatAmount(params.value, 2, true),
		},
		{
			field: 'totalNav',
			headerName: t(investmentFundOperationsLang.column_total_nav.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => (
				<AppFormatAmount value={params.value} addSpace currency={params.row.currency} />
			),
		},
		{
			field: 'nbOfShares',
			headerName: ['venture_capital', 'private_equity'].includes(investExpertiseTag)
				? t(investmentFundOperationsLang.column_total_number_of_fund_shares.tag)
				: t(investmentFundOperationsLang.column_nb_of_shares.tag),
			width: ['venture_capital', 'private_equity'].includes(investExpertiseTag) ? 160 : 130,
			align: 'right',
			headerAlign: 'right',
			flex: 1,
			renderCell: (params: GridRenderCellParams) => formatAmount(params.value, 0, true),
		},
	];

	return (
		<Box>
			{rows ? (
				<CAppDataGrid
					className="fund-share-values-table-desktop"
					pageSize={5}
					getRowClassName={(params) => `row-status--${params.row.fundIsRealized}`}
					rows={rows}
					columns={columns}
					hideFooter
					autoHeight
					disableSelectionOnClick
					disableColumnFilter
				/>
			) : (
				''
			)}
		</Box>
	);
};

const ShareValuesTableMobile = (props: ShareValuesTableProps) => {
	const { shareValues, investExpertiseTag } = props;

	const theme = useTheme();
	const { t } = useTranslation();
	const { formatAmount } = useAppFormatNumber();

	return (
		<>
			{shareValues &&
				shareValues.map((shareValue, index) => {
					return (
						<CAppPaper
							className="fund-share-values-table-mobile"
							variant="outlined"
							key={index}
							sx={{
								marginBottom: '15px',
								backgroundColor: theme.palette.mode === 'dark' ? styleVariables.primaryColor : '#FFF',
								border: '1px solid white',
							}}
						>
							<Grid container spacing={3} alignItems="center">
								<Grid item xs={6}>
									<BoxFundItem
										label={t(investmentFundOperationsLang.column_date.tag)}
										value={shareValue.reportingPeriod ? shareValue.reportingPeriod.name : ''}
									/>
								</Grid>
								{['venture_capital', 'private_equity'].includes(investExpertiseTag) ? (
									<></>
								) : (
									<Grid item xs={6}>
										<BoxFundItem
											label={t(investmentFundOperationsLang.column_nominal_price.tag)}
											value={formatAmount(shareValue.nominalPrice, 0, true)}
										/>
									</Grid>
								)}
								<Grid item xs={6}>
									<BoxFundItem
										label={t(investmentFundOperationsLang.column_nav_per_share.tag)}
										value={formatAmount(shareValue.navPerShare, 0, true)}
									/>
								</Grid>
								<Grid item xs={6}>
									<BoxFundItem
										label={t(investmentFundOperationsLang.column_total_nav.tag)}
										value={
											<AppFormatAmount
												value={shareValue.totalNav}
												addSpace
												currency={shareValue.currency}
											/>
										}
									/>
								</Grid>
								<Grid item xs={6}>
									<BoxFundItem
										label={
											['venture_capital', 'private_equity'].includes(investExpertiseTag)
												? t(investmentFundOperationsLang.column_total_number_of_fund_shares.tag)
												: t(investmentFundOperationsLang.column_nb_of_shares.tag)
										}
										value={formatAmount(shareValue.nbOfShares, 0, true)}
									/>
								</Grid>
							</Grid>
						</CAppPaper>
					);
				})}
		</>
	);
};

export default VInvestFundOperations;
